import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { todayLocalIsoDate } from '../../shared/format.ts';
import { SETTINGS_QUERY_KEY } from './settings.ts';
import {
  DeleteInvoiceResponseSchema,
  INVOICE_PAYMENT_STATUS_ERROR,
  InvoiceDetailResponseSchema,
  InvoiceListQuerySchema,
  InvoiceListResponseSchema,
  InvoicePaymentStatusInputSchema,
  InvoiceSearchInputSchema,
  type InvoiceDetailResponse,
  type InvoiceListQuery,
  type InvoiceListResponse,
  type InvoicePaymentStatusInput,
  type InvoicePartyInput,
  type InvoiceSearchInput,
  type UpsertInvoiceInput,
} from '../../shared/invoice.ts';

export const INVOICES_QUERY_KEY = ['invoices'] as const;
export const ORIGINAL_PDF_DOWNLOAD_ERROR =
  'Could not download original PDF. Try again or check the logs.';

export type InvoiceListFilters = Partial<
  Pick<InvoiceListQuery, 'currency' | 'year' | 'page' | 'pageSize'>
>;
export type InvoiceSearchFilters = Partial<
  Pick<InvoiceSearchInput, 'q' | 'currency' | 'year' | 'page' | 'pageSize'>
>;

export interface UpdateInvoiceInput {
  id: string;
  invoice: UpsertInvoiceInput;
}

export interface UpdateInvoicePaymentStatusInput extends InvoicePaymentStatusInput {
  id: string;
}

export function invoicesListQueryKey(filters: InvoiceListFilters = {}) {
  return [...INVOICES_QUERY_KEY, 'list', normalizeListFilters(filters)] as const;
}

export function invoiceSearchQueryKey(input: InvoiceSearchFilters = {}) {
  return [...INVOICES_QUERY_KEY, 'search', normalizeSearchKey(input)] as const;
}

export function invoiceDetailQueryKey(id: string) {
  return [...INVOICES_QUERY_KEY, 'detail', id] as const;
}

export function useInvoices(
  filters: InvoiceListFilters = {},
): UseQueryResult<InvoiceListResponse, Error> {
  return useQuery<InvoiceListResponse, Error>({
    queryKey: invoicesListQueryKey(filters),
    queryFn: () => fetchInvoices(filters),
    retry: false,
  });
}

export function useInvoiceSearch(
  input: InvoiceSearchFilters = {},
): UseQueryResult<InvoiceListResponse, Error> {
  const normalizedInput = normalizeSearchInput(input);
  const searchRequestSignature = [
    normalizedInput.q,
    normalizedInput.currency ?? '',
    normalizedInput.year,
    normalizedInput.page,
    normalizedInput.pageSize,
  ].join('\u0000');
  const requestKey = useMemo(
    () => createSearchRequestKey(searchRequestSignature),
    [searchRequestSignature],
  );

  return useQuery<InvoiceListResponse, Error>({
    queryKey: [...invoiceSearchQueryKey(normalizedInput), requestKey],
    queryFn: () => searchInvoices(normalizedInput),
    retry: false,
    gcTime: 0,
  });
}

export function useInvoice(id: string): UseQueryResult<InvoiceDetailResponse, Error> {
  return useQuery<InvoiceDetailResponse, Error>({
    queryKey: invoiceDetailQueryKey(id),
    queryFn: () => fetchInvoiceDetailForDownload(id),
    retry: false,
    enabled: id.trim() !== '',
  });
}

export function useCreateInvoice(): UseMutationResult<
  InvoiceDetailResponse,
  Error,
  UpsertInvoiceInput
> {
  const queryClient = useQueryClient();
  return useMutation<InvoiceDetailResponse, Error, UpsertInvoiceInput>({
    mutationFn: createInvoice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
  });
}

export function useUpdateInvoice(): UseMutationResult<
  InvoiceDetailResponse,
  Error,
  UpdateInvoiceInput
> {
  const queryClient = useQueryClient();
  return useMutation<InvoiceDetailResponse, Error, UpdateInvoiceInput>({
    mutationFn: updateInvoice,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: invoiceDetailQueryKey(data.invoice.id) });
    },
  });
}

export function useDeleteInvoice(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteInvoice,
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      queryClient.removeQueries({ queryKey: invoiceDetailQueryKey(id) });
    },
  });
}

export function useDuplicateInvoice(): UseMutationResult<InvoiceDetailResponse, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<InvoiceDetailResponse, Error, string>({
    mutationFn: duplicateInvoice,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: invoiceDetailQueryKey(data.invoice.id) });
    },
  });
}

export function useUpdateInvoicePaymentStatus(): UseMutationResult<
  InvoiceDetailResponse,
  Error,
  UpdateInvoicePaymentStatusInput
> {
  const queryClient = useQueryClient();
  return useMutation<InvoiceDetailResponse, Error, UpdateInvoicePaymentStatusInput>({
    mutationFn: updateInvoicePaymentStatus,
    onSuccess: async (data) => {
      queryClient.setQueryData(invoiceDetailQueryKey(data.invoice.id), data);
      await queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
    },
  });
}

async function fetchInvoices(filters: InvoiceListFilters): Promise<InvoiceListResponse> {
  normalizeListFilters(filters);
  const path = buildInvoiceListPath(filters);
  const response = await fetch(path, jsonRequest('GET'));

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `GET /api/invoices failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return InvoiceListResponseSchema.parse(payload);
}

async function searchInvoices(input: InvoiceSearchFilters): Promise<InvoiceListResponse> {
  const payload = normalizeSearchInput(input);
  const response = await fetch('/api/invoices/search', {
    ...jsonRequest('POST'),
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(
        response,
        `POST /api/invoices/search failed with status ${response.status}`,
      ),
    );
  }

  const responsePayload: unknown = await response.json();
  return InvoiceListResponseSchema.parse(responsePayload);
}

export async function fetchInvoiceDetailForDownload(id: string): Promise<InvoiceDetailResponse> {
  const path = invoiceDetailPath(id);
  const response = await fetch(path, jsonRequest('GET'));

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `GET ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return InvoiceDetailResponseSchema.parse(payload);
}

export async function downloadOriginalInvoicePdf(id: string): Promise<void> {
  const path = `${invoiceDetailPath(id)}/original`;
  let objectUrl: string | null = null;
  let anchor: HTMLAnchorElement | null = null;
  let failed = false;

  try {
    const response = await fetch(path, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { accept: 'application/pdf' },
    });

    if (!response.ok) throw new Error(ORIGINAL_PDF_DOWNLOAD_ERROR);

    const filename = filenameFromContentDisposition(response.headers.get('content-disposition'));
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.target = '_blank';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
  } catch {
    failed = true;
  } finally {
    try {
      anchor?.remove();
    } catch {
      failed = true;
    }

    if (objectUrl) {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        failed = true;
      }
    }
  }

  if (failed) throw new Error(ORIGINAL_PDF_DOWNLOAD_ERROR);
}

async function createInvoice(input: UpsertInvoiceInput): Promise<InvoiceDetailResponse> {
  const response = await fetch('/api/invoices', {
    ...jsonRequest('POST'),
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(toUpsertPayload(input)),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `POST /api/invoices failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return InvoiceDetailResponseSchema.parse(payload);
}

async function updateInvoice(input: UpdateInvoiceInput): Promise<InvoiceDetailResponse> {
  const path = invoiceDetailPath(input.id);
  const response = await fetch(path, {
    ...jsonRequest('PUT'),
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(toUpsertPayload(input.invoice)),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `PUT ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return InvoiceDetailResponseSchema.parse(payload);
}

async function deleteInvoice(id: string): Promise<void> {
  const path = invoiceDetailPath(id);
  const response = await fetch(path, jsonRequest('DELETE'));

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `DELETE ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  DeleteInvoiceResponseSchema.parse(payload);
}

async function duplicateInvoice(id: string): Promise<InvoiceDetailResponse> {
  const path = `${invoiceDetailPath(id)}/duplicate`;
  const response = await fetch(path, {
    ...jsonRequest('POST'),
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ date: todayLocalIsoDate() }),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `POST ${path} failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return InvoiceDetailResponseSchema.parse(payload);
}

async function updateInvoicePaymentStatus(
  input: UpdateInvoicePaymentStatusInput,
): Promise<InvoiceDetailResponse> {
  const path = `${invoiceDetailPath(input.id)}/payment-status`;
  const body = JSON.stringify(toPaymentStatusPayload(input));
  let response: Response;

  try {
    response = await fetch(path, {
      ...jsonRequest('PATCH'),
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body,
    });
  } catch {
    throw new Error(INVOICE_PAYMENT_STATUS_ERROR);
  }

  if (!response.ok) throw new Error(INVOICE_PAYMENT_STATUS_ERROR);

  const payload: unknown = await response.json();
  return InvoiceDetailResponseSchema.parse(payload);
}

function normalizeListFilters(filters: InvoiceListFilters): InvoiceListQuery {
  return InvoiceListQuerySchema.parse({
    currency: filters.currency,
    year: filters.year,
    page: filters.page,
    pageSize: filters.pageSize,
  });
}

function normalizeSearchInput(input: InvoiceSearchFilters): InvoiceSearchInput {
  return InvoiceSearchInputSchema.parse({
    q: input.q,
    currency: input.currency,
    year: input.year,
    page: input.page,
    pageSize: input.pageSize,
  });
}

function normalizeSearchKey(input: InvoiceSearchFilters): Omit<InvoiceSearchInput, 'q'> {
  const normalized = normalizeSearchInput(input);
  return {
    currency: normalized.currency,
    year: normalized.year,
    page: normalized.page,
    pageSize: normalized.pageSize,
  };
}

function createSearchRequestKey(_searchRequestSignature: string): string {
  return `request-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildInvoiceListPath(filters: InvoiceListFilters): string {
  const params = new URLSearchParams();
  appendParam(params, 'currency', filters.currency);
  appendParam(params, 'year', filters.year);
  appendParam(params, 'page', filters.page);
  appendParam(params, 'pageSize', filters.pageSize);
  const query = params.toString();
  return query ? `/api/invoices?${query}` : '/api/invoices';
}

function appendParam(params: URLSearchParams, key: string, value: string | number | undefined) {
  if (value !== undefined) params.set(key, String(value));
}

function invoiceDetailPath(id: string): string {
  return `/api/invoices/${encodeURIComponent(id)}`;
}

function filenameFromContentDisposition(value: string | null): string {
  if (!value) return 'archive-original.pdf';

  const encodedMatch = /(?:^|;)\s*filename\*=UTF-8''([^;]+)/i.exec(value);
  if (encodedMatch?.[1]) {
    try {
      return sanitizePdfFilename(decodeURIComponent(encodedMatch[1]));
    } catch {
      return 'archive-original.pdf';
    }
  }

  const quotedMatch = /(?:^|;)\s*filename="([^"]+)"/i.exec(value);
  if (quotedMatch?.[1]) return sanitizePdfFilename(quotedMatch[1]);

  const plainMatch = /(?:^|;)\s*filename=([^;]+)/i.exec(value);
  if (plainMatch?.[1]) return sanitizePdfFilename(plainMatch[1]);

  return 'archive-original.pdf';
}

function sanitizePdfFilename(value: string): string {
  const leaf = value.split(/[\\/]/).pop()?.trim() ?? '';
  const withoutControls = Array.from(leaf)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return (code >= 32 && code !== 127) || code > 127;
    })
    .join('');
  const safe = withoutControls
    .replace(/[^A-Za-z0-9._ -]/g, '_')
    .replace(/^[.\s]+/, '')
    .trim();
  if (!safe) return 'archive-original.pdf';
  return safe.toLowerCase().endsWith('.pdf') ? safe : `${safe}.pdf`;
}

function jsonRequest(method: string): RequestInit {
  return {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  };
}

function toUpsertPayload(input: UpsertInvoiceInput): UpsertInvoiceInput {
  return {
    date: input.date,
    dueDate: input.dueDate,
    currency: input.currency,
    billTo: toPartyPayload(input.billTo),
    shipTo: toPartyPayload(input.shipTo),
    notes: input.notes,
    items: input.items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
    })),
    taxRate: input.taxRate,
    taxLabel: input.taxLabel,
  };
}

function toPaymentStatusPayload(input: UpdateInvoicePaymentStatusInput): InvoicePaymentStatusInput {
  return InvoicePaymentStatusInputSchema.parse({ isPaid: input.isPaid });
}

function toPartyPayload(input: InvoicePartyInput): InvoicePartyInput {
  if (input.type === 'selectCustomer') {
    return { type: 'selectCustomer', customerId: input.customerId };
  }
  if (input.type === 'keepSnapshot') return { type: 'keepSnapshot' };
  if (input.type === 'clear') return { type: 'clear' };
  return { type: 'none' };
}

async function readApiError(response: Response, fallback: string): Promise<string> {
  try {
    const payload: unknown = await response.json();
    if (isApiError(payload)) {
      return payload.error;
    }
  } catch {
    // Keep the method/path/status fallback for non-JSON responses.
  }
  return fallback;
}

function isApiError(value: unknown): value is { error: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error?: unknown }).error === 'string'
  );
}
